import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface IInteraction {
  _id?: string;
  refNo?: string;
  interactionType: string;
  interactionDate: Date;
  interactionTime: string;
  interactionStatus: string;
  interactionNotes?: string;
  subscriptionId?: string; 
  documents?: string[];
  reservationStatus?: string;
  reservationId: string | {
    _id: string;
    userId: string;
    customerId: {
      _id: string;
      businessName: string;
      address?: {
        streetAddress?: string;
        city?: string;
        state?: string;
        country?: string;
        formattedAddress?: string;
      };
    };
    status: string;
  };
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

@Injectable({
  providedIn: 'root'
})
export class InteractionService {
  private apiUrl = `${environment.apiUrl}/interactions`;

  constructor(private http: HttpClient) { }

  createInteraction(interactionData: IInteraction): Observable<IInteraction> {
    return this.http.post<ApiResponse<IInteraction>>(`${this.apiUrl}/create`, interactionData)
      .pipe(map(response => response.data));
  }

  getAllInteractions(filters?: any): Observable<IInteraction[]> {
    let params = '';
    if (filters) {
      const queryParams = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          queryParams.append(key, filters[key]);
        }
      });
      params = queryParams.toString();
    }
    return this.http.get<ApiResponse<IInteraction[]>>(`${this.apiUrl}?${params}`)
      .pipe(map(response => response.data));
  }

  getInteractionById(id: string): Observable<IInteraction> {
    return this.http.get<ApiResponse<IInteraction>>(`${this.apiUrl}/${id}`)
      .pipe(map(response => response.data));
  }

  getInteractionsByReservationId(reservationId: string): Observable<IInteraction[]> {
    return this.http.get<ApiResponse<IInteraction[]>>(`${this.apiUrl}/reservation/${reservationId}`)
      .pipe(map(response => response.data));
  }

  getInteractionsByUserId(userId: string): Observable<IInteraction[]> {
    return this.http.get<ApiResponse<IInteraction[]>>(`${this.apiUrl}/user/${userId}`)
      .pipe(map(response => response.data));
  }

  updateInteraction(id: string, interactionData: Partial<IInteraction>): Observable<IInteraction> {
    return this.http.put<ApiResponse<IInteraction>>(`${this.apiUrl}/${id}`, interactionData)
      .pipe(map(response => response.data));
  }

  updateAllInteractionsToCompleted(reservationId: string): Observable<any> {
    return this.http.put<ApiResponse<any>>(`${this.apiUrl}/reservation/${reservationId}/complete-all`, {})
      .pipe(map(response => response.data));
  }
}
